import React, { useState } from "react";
import { Flex, Input, Button, Select, notification, Form, Switch } from "antd";
import { useQuery } from "@tanstack/react-query";
import CustomFilterSelect from "../../Component/CustomFilterSelect";
import { fetchUserForSearch } from "../../Services/user_question_history";
import { sendMassNotification, getAllPlan } from "../../Services/subscription";
import { getActiveMasterTag } from "../../Services/tags"
import TagSelect from "../../Component/MasterTag/";
import { Modal } from "antd";

// "Free Trial" isn't a purchased plan - it's the absence of one - so it's
// never a row in credit_subscription_plans and has to be added by hand
// alongside whatever real plans come back from the API.
const FREE_TRIAL_PLAN_OPTION = { label: "Free Trial", value: "free_trial" };

const { TextArea } = Input;

const MassNotification = ({
    isModalVisible,
    handleOk,
    handleCancel}) => {
    const [form] = Form.useForm();
    const [recipientType, setRecipientType] = useState("all");
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [planType, setPlanType] = useState(null);
    
    const [selectedTags, setSelectedTags] = useState([]);
    const [feedbackEnabled, setFeedbackEnabled] = useState(false);
    const userTagOptions = [];

    // Plan list is now the live credit_subscription_plans data (the old
    // hardcoded basic/plus/pro/ai/enterprise set no longer matches what
    // users can actually be subscribed to).
    const { data: planListData } = useQuery({
        queryKey: ["credit_plans_for_mass_notification"],
        queryFn: () => getAllPlan({ limit: 50, active: [true] }),
        enabled: isModalVisible,
        refetchOnWindowFocus: false,
        retry: false,
    });
    const planOptions = [
        FREE_TRIAL_PLAN_OPTION,
        ...(planListData?.plans || []).map((plan) => ({ label: plan.name, value: plan.plan_slug })),
    ];

    const resetForm = () => {
        form.resetFields();
        setRecipientType("all");
        setSelectedUserIds([]);
        setSelectedTags([]);
        setFeedbackEnabled(false);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (recipientType === "specific" && selectedUserIds.length === 0) {
                return notification.error({
                    message: "Please select at least one user",
                });
            }
            if (recipientType === "subscribers" && !planType) {
                return notification.error({
                    message: "Please select at least one plan",
                });
            }
            const filtered_user_id = [...selectedUserIds].filter(a => Number.isInteger(a));
            
            if (recipientType === "specific" && filtered_user_id.length === 0) {
                return notification.error({
                    message: "Please select at least one valid email",
                });
            }

            const payload = {
                title: values.title,
                body: values.body,
                url: values.url,
                recipientType,
                userIds: recipientType === "specific" ? filtered_user_id : [],
                plan_type:planType,
                tag_ids: selectedTags,
                campaign_title: values.campaign_title,
                feedback_enabled: feedbackEnabled,
            };
            setIsSending(true);
            await sendMassNotification(payload);
            resetForm();
            notification.success({
                message: "Notification sent successfully",
            });
            handleOk()
        } catch (error) {
            console.error("Validation or submission failed:", error);
            // if (error.errorFields) {
            //     notification.error({
            //         message: "Please fix form validation errors",
            //     });
            // } else {
                notification.error({
                    message: "Failed to send notification",
                });
            // }
        } finally {
            setIsSending(false);
        }
    };


    return (
        <Modal
            title="Send Mass Notification"
            open={isModalVisible}
            footer={null}
            width={500}
            onCancel={() => {
                resetForm();
                handleCancel();
            }}
            maskClosable={false}
            >

            <div>
                {/* <Flex justify="space-between" align="center">
                    <h3>Send Mass Notification</h3>
                </Flex> */}

                <br />

                <Form form={form} layout="vertical" > {/*style={{ maxWidth: 600 }} */}
                    <Form.Item
                        label="Campaign Title (optional)"
                        name="campaign_title"
                        rules={[{ max: 255, message: "Campaign Title cannot exceed 255 characters" }]}
                    >
                        <Input placeholder="Enter a label to group/search this send by" maxLength={255} />
                    </Form.Item>

                    <Form.Item
                        label="Notification Title"
                        name="title"
                        rules={[{ required: true, message: "Title is required" },
                            { max: 75, message: "Notification Title cannot exceed 75 characters" },
                            {
                            validator: (_, value) =>
                                value && value.trim() !== ""
                                ? Promise.resolve()
                                : Promise.reject("Title cannot be empty or spaces only"),
                            },
                        ]}
                    >
                        <Input placeholder="Enter Notification Title" maxLength={75} />
                    </Form.Item>

                    <Form.Item
                        label="Notification Description"
                        name="body"
                        rules={[{ required: true, message: "Description is required" },
                            { max: 150, message: "Notification description cannot exceed 150 characters" },
                            {
                                validator: (_, value) =>
                                    value && value.trim() !== ""
                                    ? Promise.resolve()
                                    : Promise.reject("Description cannot be empty or spaces only"),
                            },
                        ]}
                    >
                        <TextArea rows={4} placeholder="Enter Notification Description" maxLength={150} />
                    </Form.Item>

                    <Form.Item
                        label="Url"
                        name="url"
                        rules={[
                            { type: "url", message: "Please enter a valid URL (including http/https)" },
                        ]}
                    >
                        <Input placeholder="Enter Url" />
                    </Form.Item>


                    <Form.Item label="Recipient Type">
                        <Select
                            value={recipientType}
                            onChange={(val) => {
                                setRecipientType(val);
                                if (['all', 'subscribers', 'guest', 'registered'].includes(val)) setSelectedUserIds([]); form.resetFields(['specific_users'])
                                if (['all', 'specific', 'guest', 'registered'].includes(val)) setPlanType(null); form.resetFields(['plan_type'])
                                if (['all', 'subscribers', 'specific', 'guest', 'registered'].includes(val)) setSelectedTags([]); form.resetFields(['selected_master_tag']);
                            }}
                            options={[
                                { label: "All Users", value: "all" },
                                { label: "Specific Users", value: "specific" },
                                { label: "Plan Subscribers", value: "subscribers" },
                                { label: "Guest/Logout Users", value: "guest"},
                                { label: "Registered User", value: "registered"},
                                { label: "User Tags", value: "user_tags"}
                            ]}
                        />
                    </Form.Item>

                    {recipientType === "specific" && (
                        <Form.Item label="Select Specific Users" name="specific_users" rules={[{ required: true, message: "Specific Users is required" }]}>
                            <CustomFilterSelect
                                fetchOptions={fetchUserForSearch}
                                label="Users"
                                filterKey="user"
                                onChange={(key, value) => setSelectedUserIds(value)}
                                renderOption={(option) =>
                                    `#${option.value} ${option.label} (${option.email})`
                                }
                                className="custom-user-select"
                                mode="multiple"
                                value={selectedUserIds}
                                copyPaste={true}
                            />
                        </Form.Item>
                    )}
                    
                    {recipientType === "subscribers" && (
                        <Form.Item label="Plan Type" name="plan_type" rules={[{ required: true, message: "Plan Type is required" }]}>
                            <Select
                                onChange={(value) => setPlanType(value)}
                                options={planOptions}
                                placeholder="Select Plan Type"
                            />
                        </Form.Item>
                    )}


                    {
                        recipientType === 'user_tags' && (
                            <Form.Item label="Select Tag" name="selected_master_tag" rules={[{ required: true, message: "Tag is required" }]}>
                                <TagSelect
                                    value={selectedTags}
                                    onChange={setSelectedTags}
                                    fetchTags={getActiveMasterTag}
                                    userTagOptions={userTagOptions}
                                />
                            </Form.Item>
                        )
                    }
                    <Form.Item label="Ask recipients for feedback">
                        <Switch checked={feedbackEnabled} onChange={setFeedbackEnabled} />
                        <span style={{ marginLeft: 10, color: "#64748b" }}>
                            When on, the app prompts the recipient for usefulness feedback
                        </span>
                    </Form.Item>
                    <Form.Item>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                loading={isSending}
                                style={{ color: "white" }}
                            >
                                Send Notification
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default MassNotification;
